import jwtDecode from 'jwt-decode';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useState } from 'react';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { observer } from 'mobx-react-lite';

import { Paper, Typography } from '@mui/material';

import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthApi } from '../ApiClient';
import { isPortrait } from '../utils';

interface ResetToken {
  email: string;
  exp: number;
}

interface UpdatePassword {
  newPassword: string;
  newPasswordRepeat: string;
  email: string;
}

const RedeemPasswordPage = observer((): JSX.Element => {
  const authApi = useAuthApi();
  const { resetToken } = useParams();
  const navigate = useNavigate();
  const decodedToken = jwtDecode<ResetToken>(resetToken || '');

  const [errorMsg, setErrorMsg] = useState('');

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isValid },
  } = useForm<UpdatePassword>({
    mode: 'onChange',
  });

  const updatePassword = async () => {
    try {
      const newPassword = getValues('newPassword');
      const email = getValues('email');

      await authApi.redeemPasswordToken({
        token: resetToken || '',
        username: email,
        password: newPassword,
      });

      navigate('/login');
    } catch (error) {
      setErrorMsg('* Passwort konnte nicht geändert werden');
    }
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '85vh',
      }}
    >
      <Box
        sx={{
          display: isPortrait() ? 'block' : 'flex',
        }}
      >
        <Paper sx={{ padding: 1 }} elevation={0}>
          <Typography
            component="h1"
            variant="h4"
            align="center"
            color="secondary"
          >
            Neues Passwort wählen
          </Typography>
          <Controller
            name="email"
            control={control}
            defaultValue={decodedToken.email}
            rules={{ required: 'Pflichtfeld!' }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                sx={{
                  marginTop: 2,
                  marginBottom: 2,
                  input: { color: '#58585A' },
                }}
                margin="dense"
                id="content"
                label="Email-Adresse"
                type="email"
                fullWidth
                variant="standard"
                inputProps={{
                  maxLength: 150,
                  style: { color: '#58585A' },
                }}
                value={value}
                error={!!error}
                onChange={onChange}
                helperText={error ? error.message : null}
                required
              />
            )}
          />
          <Controller
            name="newPassword"
            control={control}
            rules={{
              validate: value => {
                if (!value) return 'Pflichtfeld!';

                const errors = [];
                if (value.length < 8) errors.push('mindestens 8 Zeichen');
                if (!/\d/.test(value)) errors.push('eine Zahl');
                if (!/[A-Z]/.test(value)) errors.push('einen Großbuchstaben');
                if (!/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/.test(value))
                  errors.push('ein Sonderzeichen');

                return errors.length
                  ? `Passwort muss ${errors.join(', ')} enthalten`
                  : true;
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                sx={{
                  marginTop: 2,
                  marginBottom: 2,
                  input: { color: '#58585A' },
                }}
                margin="dense"
                id="content"
                label="Neues Passwort"
                type="password"
                fullWidth
                variant="standard"
                inputProps={{
                  style: { color: '#58585A' },
                }}
                value={value}
                error={!!error}
                onChange={onChange}
                helperText={error ? error.message : null}
                required
              />
            )}
          />

          <Controller
            name="newPasswordRepeat"
            control={control}
            rules={{
              validate: value => {
                if (!value) return 'Pflichtfeld!';

                const errors = [];
                if (value.length < 8) errors.push('mindestens 8 Zeichen');
                if (!/\d/.test(value)) errors.push('eine Zahl');
                if (!/[A-Z]/.test(value)) errors.push('einen Großbuchstaben');
                if (!/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/.test(value))
                  errors.push('ein Sonderzeichen');

                return errors.length
                  ? `Passwort muss ${errors.join(', ')} enthalten`
                  : true;
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                sx={{
                  marginTop: 2,
                  marginBottom: 2,
                  input: { color: '#58585A' },
                }}
                margin="dense"
                id="content"
                label="Neues Passwort wiederholen"
                type="password"
                fullWidth
                variant="standard"
                inputProps={{
                  style: { color: '#58585A' },
                }}
                value={value}
                error={!!error}
                onChange={onChange}
                helperText={error ? error.message : null}
                required
              />
            )}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={!isValid}
              onClick={handleSubmit(updatePassword)}
              sx={{ paddingX: 4, mt: 3, mb: 2 }}
            >
              Passwort aktualisieren
            </Button>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
            <Typography
              component="span"
              variant="subtitle1"
              align="center"
              color="primary.main"
            >
              {`${errorMsg}`}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
});

export default RedeemPasswordPage;
